/**
 * 능력치 창. 원작 스탯 창(S)의 배치를 참고해 STR/DEX/INT/LUK 와 파생 수치를 보여 준다.
 */

import Phaser from 'phaser';
import { Sfx } from '../audio/Sfx';
import {
  PRIMARY_STATS,
  PRIMARY_STAT_LABEL,
  type PrimaryStat,
} from '../data/balance';
import { weaponSpeedLabel } from '../data/weapons';
import { EV } from '../game/events';
import { sumWorn } from '../systems/gear';
import type { PlayerState } from '../systems/PlayerState';
import { COLOR, TEXT_COLOR, UI_FONT, UI_MONO } from './theme';

const PAD = 14;
const WIDTH = 300;
const HEADER = 30;
const ROW_H = 28;
const FOOTER = 154;
const STAT_TOP = HEADER + 88;
const HEIGHT = STAT_TOP + PRIMARY_STATS.length * ROW_H + 28 + FOOTER;

export class StatWindow {
  private readonly container: Phaser.GameObjects.Container;
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly infoText: Phaser.GameObjects.Text;
  private readonly hpMpText: Phaser.GameObjects.Text;
  private readonly apText: Phaser.GameObjects.Text;
  private readonly derivedText: Phaser.GameObjects.Text;
  private readonly hintText: Phaser.GameObjects.Text;
  private readonly valueTexts: Record<PrimaryStat, Phaser.GameObjects.Text>;
  private readonly plusButtons: Record<PrimaryStat, Phaser.GameObjects.Text>;

  constructor(
    scene: Phaser.Scene,
    private readonly state: PlayerState,
  ) {
    const x = 24;
    const y = 70;
    this.container = scene.add.container(x, y).setVisible(false);

    this.g = scene.add.graphics();
    this.container.add(this.g);

    const title = scene.add.text(PAD, 8, '능력치', {
      fontFamily: UI_FONT,
      fontSize: '15px',
      color: TEXT_COLOR.bright,
    });
    this.container.add(title);

    const closeBg = scene.add.rectangle(WIDTH - PAD - 8, HEADER / 2, 22, 20, COLOR.windowDark);
    closeBg.setOrigin(0.5).setInteractive({ useHandCursor: true });
    const hint = scene.add.text(WIDTH - PAD - 8, HEADER / 2, '✕', {
      fontFamily: UI_MONO,
      fontSize: '12px',
      color: TEXT_COLOR.dark,
    });
    hint.setOrigin(0.5);
    closeBg.on('pointerdown', () => this.close());
    hint.setInteractive({ useHandCursor: true }).on('pointerdown', () => this.close());
    this.container.add([closeBg, hint]);

    this.infoText = scene.add.text(PAD, HEADER + 10, '', {
      fontFamily: UI_FONT,
      fontSize: '14px',
      color: TEXT_COLOR.dark,
      wordWrap: { width: WIDTH - PAD * 2, useAdvancedWrap: true },
    });
    this.hpMpText = scene.add.text(PAD, HEADER + 32, '', {
      fontFamily: UI_FONT,
      fontSize: '13px',
      color: TEXT_COLOR.darkDim,
      lineSpacing: 4,
      wordWrap: { width: WIDTH - PAD * 2, useAdvancedWrap: true },
    });
    this.container.add([this.infoText, this.hpMpText]);

    const statTop = STAT_TOP;
    this.valueTexts = {} as Record<PrimaryStat, Phaser.GameObjects.Text>;
    this.plusButtons = {} as Record<PrimaryStat, Phaser.GameObjects.Text>;

    PRIMARY_STATS.forEach((stat, i) => {
      const yy = statTop + i * ROW_H;
      const label = scene.add.text(PAD, yy + 4, PRIMARY_STAT_LABEL[stat], {
        fontFamily: UI_MONO,
        fontSize: '14px',
        fontStyle: 'bold',
        color: TEXT_COLOR.dark,
      });
      const value = scene.add.text(88, yy + 4, '0', {
        fontFamily: UI_MONO,
        fontSize: '14px',
        color: TEXT_COLOR.dark,
      });
      const plusHit = scene.add.rectangle(WIDTH - PAD - 16, yy + 12, 26, 22, COLOR.windowDark, 0);
      plusHit.setInteractive({ useHandCursor: true });
      plusHit.on('pointerdown', () => this.allocate(stat));
      const plus = scene.add.text(WIDTH - PAD - 16, yy + 12, '+', {
        fontFamily: UI_MONO,
        fontSize: '18px',
        color: TEXT_COLOR.dark,
      });
      plus.setOrigin(0.5);
      plus.setInteractive({ useHandCursor: true });
      plus.on('pointerdown', () => this.allocate(stat));
      this.valueTexts[stat] = value;
      this.plusButtons[stat] = plus;
      this.container.add([label, value, plusHit, plus]);
    });

    this.apText = scene.add.text(PAD, statTop + PRIMARY_STATS.length * ROW_H + 4, '', {
      fontFamily: UI_FONT,
      fontSize: '14px',
      color: TEXT_COLOR.dark,
    });
    this.derivedText = scene.add.text(PAD, HEIGHT - FOOTER + 12, '', {
      fontFamily: UI_FONT,
      fontSize: '12px',
      color: TEXT_COLOR.dark,
      lineSpacing: 3,
      wordWrap: { width: WIDTH - PAD * 2, useAdvancedWrap: true },
    });
    this.hintText = scene.add.text(PAD, HEIGHT - 36, '', {
      fontFamily: UI_FONT,
      fontSize: '11px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - PAD * 2 },
    });
    this.container.add([this.apText, this.derivedText, this.hintText]);

    this.state.events.on(EV.statsChanged, this.refresh, this);
    this.state.events.on(EV.hpChanged, this.refresh, this);
    this.state.events.on(EV.mpChanged, this.refresh, this);
    this.state.events.on(EV.expChanged, this.refresh, this);

    this.drawFrame();
    this.refresh();
  }

  destroy(): void {
    this.state.events.off(EV.statsChanged, this.refresh, this);
    this.state.events.off(EV.hpChanged, this.refresh, this);
    this.state.events.off(EV.mpChanged, this.refresh, this);
    this.state.events.off(EV.expChanged, this.refresh, this);
  }

  get visible(): boolean {
    return this.container.visible;
  }

  toggle(): boolean {
    return this.setOpen(!this.container.visible);
  }

  close(): boolean {
    return this.setOpen(false);
  }

  private setOpen(open: boolean): boolean {
    this.container.setVisible(open);
    if (open) this.refresh();
    return open;
  }

  private allocate(stat: PrimaryStat): void {
    if (this.state.allocateAp(stat)) Sfx.uiClick();
    else Sfx.deny();
  }

  private drawFrame(): void {
    const g = this.g;
    g.clear();
    g.fillStyle(COLOR.windowEdge, 0.95);
    g.fillRoundedRect(-3, -3, WIDTH + 6, HEIGHT + 6, 8);
    g.fillStyle(COLOR.window, 1);
    g.fillRoundedRect(0, 0, WIDTH, HEIGHT, 6);
    g.fillStyle(COLOR.windowEdge, 1);
    g.fillRoundedRect(0, 0, WIDTH, HEADER, 6);
    g.lineStyle(1, COLOR.slotEdge, 1);
    g.lineBetween(PAD, HEIGHT - FOOTER, WIDTH - PAD, HEIGHT - FOOTER);
  }

  private refresh = (): void => {
    if (!this.container.scene) return;
    const { state } = this;
    const s = state.stats;
    const canSpend = state.ap.unspent > 0;

    this.infoText.setText(`${state.name}  ·  Lv.${state.level} ${state.job.name}`);
    this.hpMpText.setText(`HP ${state.hp} / ${s.maxHp}\nMP ${state.mp} / ${s.maxMp}`);
    this.apText.setText(`남은 AP  ${state.ap.unspent}`);
    this.apText.setColor(canSpend ? '#8a3a12' : TEXT_COLOR.darkDim);

    for (const stat of PRIMARY_STATS) {
      this.valueTexts[stat].setText(`${state.ap[stat]}`);
      this.plusButtons[stat].setAlpha(canSpend ? 1 : 0.28);
      this.plusButtons[stat].setColor(canSpend ? '#8a3a12' : TEXT_COLOR.darkDim);
    }

    this.derivedText.setText(
      [
        `공격력  ${s.attackMin} ~ ${s.attackMax}`,
        `방어력  ${s.defense}`,
        `명중 ${s.accuracy}  /  회피 ${s.avoid}`,
        `크리티컬  ${(s.critRate * 100).toFixed(1)}%  ×${s.critMultiplier}`,
        `무기 ${state.weapon.name}  (${weaponSpeedLabel(state.weapon.speed)})`,
        `장비 공+${sumWorn(state.worn).watk} 방+${sumWorn(state.worn).def} 명+${sumWorn(state.worn).acc}`,
      ].join('\n'),
    );
    this.hintText.setText('STR HP·공격  DEX 명중·회피  INT MP  LUK 크리');
  };
}
